import React from 'react';
import { useReservations } from '../ReservationContext';

const DashboardView = ({ onArrival, onShowWaitlist }) => {
  const {
    currentTime,
    dashboardData,
    waitlist,
    zones,
    tables,
    searchQuery,
    setSearchQuery,
    searchResults,
    searchReservations,
    getServicePeriod,
    updateReservationStatus,
    setError,
    loading,
  } = useReservations();

  const tablesByZone = {};
  zones.forEach(zone => {
    tablesByZone[zone.zone_id] = tables.filter(t => t.zone_id === zone.zone_id);
  });

  const availableTables = tables.filter(t => t.status === 'available' && t.is_active);
  const seatedTables = tables.filter(t => t.status === 'occupied' || t.status === 'seated');
  const reservedTables = tables.filter(t => t.status === 'reserved');

  const seatFromWaitlist = async (item) => {
    await updateReservationStatus(item, 'seated');
  };

  const removeFromWaitlist = async (item) => {
    if (!window.confirm(`Remove ${item.customer_name} from waitlist?`)) return;
    await updateReservationStatus(item, 'cancelled');
  };

  return (
    <div className="dashboard-view">
      {/* Header with time and service period */}
      <div className="dashboard-header">
        <div className="dashboard-time">
          <span className="current-time">
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span className={`service-period period-${getServicePeriod().toLowerCase()}`}>
            {getServicePeriod()}
          </span>
        </div>
        <div className="dashboard-actions">
          <button className="btn btn-primary btn-large" onClick={onShowWaitlist}>
            + Walk-In
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="dashboard-search">
        <input
          type="text"
          placeholder="Search by name, phone, or time..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            searchReservations(e.target.value);
          }}
          className="search-input-large"
        />
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map(r => (
              <div key={r.reservation_id} className="search-result-item" onClick={() => onArrival(r)}>
                <div className="search-result-main">
                  <span className="search-result-name">{r.customer_name}</span>
                  <span className="search-result-time">{r.time}</span>
                  <span className="search-result-party">{r.party_size} guests</span>
                </div>
                <span className={`status-badge status-${r.status}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Left Column - Tables Overview */}
        <div className="dashboard-section tables-overview">
          <h3>Tables</h3>
          <div className="table-status-cards">
            <div className="status-card status-available">
              <span className="status-count">{availableTables.length}</span>
              <span className="status-label">Available</span>
            </div>
            <div className="status-card status-seated">
              <span className="status-count">{seatedTables.length}</span>
              <span className="status-label">Seated</span>
            </div>
            <div className="status-card status-reserved">
              <span className="status-count">{reservedTables.length}</span>
              <span className="status-label">Reserved</span>
            </div>
          </div>

          {/* Visual Floor Plan */}
          <div className="floor-plan">
            {zones.map(zone => (
              <div key={zone.zone_id} className="floor-zone">
                <h4>{zone.zone_name}</h4>
                <div className="zone-tables-grid">
                  {(tablesByZone[zone.zone_id] || []).map(table => (
                    <div
                      key={table.table_id}
                      className={`floor-table table-${table.status || 'available'}`}
                      title={`${table.table_id} - ${table.capacity} seats - ${table.status || 'available'}`}
                    >
                      <span className="table-number">{table.table_id}</span>
                      <span className="table-seats">{table.capacity}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Covers */}
          <div className="covers-display">
            <div className="covers-bar">
              <div
                className="covers-filled"
                style={{ width: `${(dashboardData.coversSeated / dashboardData.totalCapacity) * 100}%` }}
              />
            </div>
            <span className="covers-text">
              {dashboardData.coversSeated} / {dashboardData.totalCapacity} covers
            </span>
          </div>
        </div>

        {/* Middle Column - Arriving Soon */}
        <div className="dashboard-section arriving-section">
          <h3>Arriving Soon</h3>
          <div className="arriving-list">
            {dashboardData.arrivingSoon?.length === 0 ? (
              <div className="empty-state">No reservations in the next 30 minutes</div>
            ) : (
              dashboardData.arrivingSoon?.map(r => (
                <div key={r.reservation_id} className="arriving-card" onClick={() => onArrival(r)}>
                  <div className="arriving-time">{r.time}</div>
                  <div className="arriving-info">
                    <span className="arriving-name">{r.customer_name}</span>
                    <span className="arriving-party">{r.party_size} guests</span>
                    {r.special_requests && (
                      <span className="arriving-notes">{r.special_requests}</span>
                    )}
                  </div>
                  <button
                    className="btn btn-small btn-success"
                    onClick={(e) => { e.stopPropagation(); onArrival(r); }}
                  >
                    Check In
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Column - Waitlist & Alerts */}
        <div className="dashboard-section">
          {/* Alerts */}
          {dashboardData.alerts?.length > 0 && (
            <div className="alerts-section">
              <h3>Alerts</h3>
              <div className="alerts-list">
                {dashboardData.alerts.map((alert, idx) => (
                  <div key={idx} className={`alert-card alert-${alert.type}`}>
                    <span className="alert-icon">
                      {alert.type === 'late' ? '⏰' : alert.type === 'vip' ? '⭐' : '⚠️'}
                    </span>
                    <span className="alert-message">{alert.message}</span>
                    {alert.reservation && (
                      <button className="btn btn-small" onClick={() => onArrival(alert.reservation)}>
                        View
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waitlist */}
          <div className="waitlist-section">
            <div className="waitlist-header">
              <h3>Waitlist ({waitlist.length})</h3>
            </div>
            <div className="waitlist-list">
              {waitlist.length === 0 ? (
                <div className="empty-state">No one waiting</div>
              ) : (
                waitlist.map((item, idx) => (
                  <div key={item.reservation_id} className="waitlist-card">
                    <div className="waitlist-position">{idx + 1}</div>
                    <div className="waitlist-info">
                      <span className="waitlist-name">{item.customer_name}</span>
                      <span className="waitlist-party">{item.party_size} guests</span>
                      <span className="waitlist-wait">
                        {Math.round((new Date() - new Date(item.created_at)) / 60000)} min wait
                      </span>
                    </div>
                    <div className="waitlist-actions">
                      <button
                        className="btn btn-small btn-success"
                        onClick={() => seatFromWaitlist(item)}
                        disabled={loading}
                      >
                        Seat
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => removeFromWaitlist(item)}
                        disabled={loading}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
